import { unwrap } from 'solid-js/store';
import * as yup from 'yup';

export const getDataValue = <T, K extends keyof T>(
  data: () => T,
  property: K,
): T[K] | undefined => data()[property];

export function deepUnwrap<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return (obj as Array<unknown>).map(deepUnwrap) as unknown as T;
  }

  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if ('subscribe' in obj) {
    obj = unwrap(obj);
  }

  const newObj: T = Array.isArray(obj) ? ([] as unknown as T) : ({} as T);

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      newObj[key as keyof T] = deepUnwrap(obj[key]);
    }
  }

  return newObj;
}

const MULTI_SPACE_REGEX = /\s{2,}/g;
export const normalizeWhitespace = (value: string): string =>
  value.replace(MULTI_SPACE_REGEX, ' ');

const EMOJI_REGEX =
  /(?:\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier}|\u200D\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier})*)*)|\p{Regional_Indicator}{2}|(?:[#*0-9]\uFE0F?\u20E3)|[\p{Emoji_Modifier}\p{Regional_Indicator}]/gu;
export const removeEmojis = (value: string): string =>
  value.replace(EMOJI_REGEX, '');

const STATE_INPUT_MAX_LENGTH = 2;
const NON_ALPHA_REGEX = /[^A-Za-z]/g;

/**
 * Sanitize a State/Province text input value.
 * - Strips any non-alphabetic characters (numbers, punctuation, whitespace)
 * - Truncates to 2 characters
 * - Uppercases the result
 *
 * Use this in `onChange` of any TextField / TextInput that captures a
 * 2-letter State or Province code.
 */
export const sanitizeStateInput = (
  value: string,
  overrideLength?: number,
): string =>
  value
    .replace(NON_ALPHA_REGEX, '')
    .slice(0, overrideLength ?? STATE_INPUT_MAX_LENGTH)
    .toUpperCase();

/*eslint-disable */
// Need to disable eslint to make function use generic type
export const createConditionalSchema = <T extends yup.Maybe<yup.AnyObject>>(
  schema: yup.ObjectSchema<T> | yup.Schema<T>,
) =>
  yup.lazy((item) =>
    item.operationType === "Delete" ? yup.object().shape({}) : schema,
  );
/*eslint-enable*/
